import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLeaderboard, LeaderboardEntry, LeaderboardPeriod } from '../api/client';

interface LeaderboardProps {
  isFullPage?: boolean;
  showTopN?: number;
}

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1: return '🥇';
    case 2: return '🥈';
    case 3: return '🥉';
    default: return `#${rank}`;
  }
};

const Leaderboard: React.FC<LeaderboardProps> = ({ isFullPage = false, showTopN = 5 }) => {
  const [activeTab, setActiveTab] = useState<LeaderboardPeriod>('daily');
  const limit = isFullPage ? 50 : showTopN;

  // Real rankings from the backend. The query key includes the period + limit,
  // so switching tabs refetches (and caches) each board independently.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['leaderboard', activeTab, limit],
    queryFn: () => getLeaderboard(activeTab, limit),
  });

  const entries: LeaderboardEntry[] = data ?? [];

  const renderBody = () => {
    if (isLoading) {
      return <div className="leaderboard-state">Loading champions…</div>;
    }
    if (isError) {
      return (
        <div className="leaderboard-state leaderboard-state-error">
          <span>Couldn’t load the leaderboard.</span>
          <button type="button" className="leaderboard-retry" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      );
    }
    if (entries.length === 0) {
      return (
        <div className="leaderboard-state">
          No champions yet — be the first to play!
        </div>
      );
    }

    return (
      <div className="leaderboard-table">
        <div className="table-header">
          <div className="header-rank">Rank</div>
          <div className="header-player">Player</div>
          <div className="header-score">Score</div>
          <div className="header-accuracy">Accuracy</div>
          {isFullPage && <div className="header-questions">Questions</div>}
        </div>

        <div className="table-body">
          {entries.map((entry) => (
            <div key={entry.address} className={`table-row ${entry.rank <= 3 ? 'top-three' : ''}`}>
              <div className="cell-rank">
                <span className="rank-display">{getRankIcon(entry.rank)}</span>
              </div>
              <div className="cell-player">
                <div className="player-info">
                  <div className="wallet-address">{entry.displayName}</div>
                </div>
              </div>
              <div className="cell-score">
                <span className="score-value">{entry.correct}</span>
                <span className="score-max">/{entry.total}</span>
              </div>
              <div className="cell-accuracy">
                <span className="accuracy-value">{entry.accuracy}%</span>
              </div>
              {isFullPage && (
                <div className="cell-questions">
                  <span className="questions-value">{entry.questionsAnswered}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`leaderboard ${isFullPage ? 'leaderboard-full' : 'leaderboard-preview'}`}>
      <div className="leaderboard-header">
        <h2 className="leaderboard-title">
          {isFullPage ? '🏆 Trivia Champions Leaderboard' : '🏆 Top Players'}
        </h2>
        {!isFullPage && (
          <a href="/leaderboard" className="view-all-link">
            View All
          </a>
        )}
      </div>

      <div className="leaderboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Daily
        </button>
        <button
          className={`tab-btn ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly
        </button>
        <button
          className={`tab-btn ${activeTab === 'alltime' ? 'active' : ''}`}
          onClick={() => setActiveTab('alltime')}
        >
          All-Time
        </button>
      </div>

      <div className="leaderboard-content">
        {renderBody()}
      </div>
    </div>
  );
};

export default Leaderboard;
